import React from "react";
import _ from "lodash";
import { connect } from "react-redux";

import { useI18n } from "Hooks";
import client from "lib/apolloClient/client";
import { compose, graphql } from "lib/apolloClient";
import { EmptyState } from "@toddle-edu/ds-web";
import { ScheduledAssignmentIllustration } from "@toddle-edu/ds-theme";
import UIModal from "UIComponents/UIModal";
import withPusherBind from "UIComponents/PusherConsumerHoc";
import FullScreenLoader from "UIComponents/FullScreenLoader";

import classes from "./SentralPortalModal.scss";
import {
  CLOSE_SENTRAL_PORTAL_IFRAME,
  SENTRAL,
  FETCH_PLATFORM_URL,
} from "Constants/stringConstants";
import { goToRelativeRoute } from "modules/Services";
import { getSelectedCurriculumId } from "modules/CommonSelectors";
import {
  verifySentralOauthRedirectQuery,
  fetchSentralUserNotificationStateQuery,
} from "modules/CommonQuery";
import { setSentralPortalDetails } from "Login/modules/LoginModule";
import {
  setIsSentralLoginActivityLogged,
  getIsSentralLoginActivityLogged,
} from "services/localStorage";
import { isInIframe, isOverviewContext } from "./SentralPortalUtils";

const SentralPortalModal = props => {
  const {
    url,
    isVisible,
    showLoader,
    setSentralPortalDetails,
    shouldShowBelowHeader = false,
    verificationFailed,
  } = props;
  const { t } = useI18n();

  const handleIframeLoad = () => {
    if (url) {
      setSentralPortalDetails({ loader: false });
    }
  };

  const handleClose = () => {
    setSentralPortalDetails({ isVisible: false });
  };

  const isStudentProfile = isOverviewContext();

  const renderStudentProfileContent = () => {
    if (!isStudentProfile) return null;

    if (verificationFailed) {
      return (
        <div className={classes.studentProfileContainer}>
          <EmptyState
            dsVersion="2.0"
            size="small"
            illustration={ScheduledAssignmentIllustration}
            title={t("common:sorry_you_dont_have_access_to_with_module_name", {
              moduleName: t("common:student_profile"),
            })}
          />
        </div>
      );
    }

    return (
      <div className={classes.studentProfileIframeContainer}>
        <iframe
          src={url}
          width="100%"
          height="100%"
          name="sentral-portal"
          onLoad={handleIframeLoad}
          title="Sentral Portal"
        />
      </div>
    );
  };

  const renderModalContent = () => {
    if (isStudentProfile) return null;

    return (
      <UIModal
        isOpen
        isFullScreen={!shouldShowBelowHeader}
        onRequestClose={handleClose}
        modalContent={
          shouldShowBelowHeader
            ? classes.modalContentBelowHeader
            : classes.modalContent
        }
      >
        <iframe
          src={url}
          name="sentral-portal"
          width="100%"
          height="100%"
          onLoad={handleIframeLoad}
          title="Sentral Portal"
        />
      </UIModal>
    );
  };

  if (!isVisible || isInIframe()) {
    return null;
  }

  return (
    <>
      {renderStudentProfileContent()}
      {renderModalContent()}
      {showLoader && <FullScreenLoader />}
    </>
  );
};

const mapStateToProps = state => {
  const sentralPortalDetails = _.get(state, "login.sentralPortalDetails", {});
  const {
    url = "",
    isVisible = false,
    loader = false,
    platformName = null,
    studentId = null,
    verificationFailed = false,
  } = sentralPortalDetails;
  const curriculumId = getSelectedCurriculumId(state);

  return {
    url,
    isVisible,
    showLoader: loader,
    platformName,
    studentId,
    curriculumId,
    verificationFailed,
  };
};

const mapActionCreators = {
  setSentralPortalDetails,
  goToRelativeRoute,
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  graphql(verifySentralOauthRedirectQuery, {
    name: "verifySentralOauthRedirect",
    skip: ({ platformName, curriculumId }) => !platformName || !curriculumId,
    options: ({ studentId, platformName, curriculumId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        input: {
          serviceName: SENTRAL,
          operationType: FETCH_PLATFORM_URL,
          platformName,
          studentId,
          curriculumId,
        },
      },
    }),
    props({ ownProps }) {
      const {
        setSentralPortalDetails,
        goToRelativeRoute,
        curriculumId,
      } = ownProps;

      const refetchNotificationStateQuery = async () => {
        await client.query({
          query: fetchSentralUserNotificationStateQuery,
          variables: {
            input: {
              curriculumIds: [curriculumId],
            },
          },
          fetchPolicy: "network-only",
        });
      };

      const handleVerificationComplete = ({
        redirectUrl,
        sentralActivity,
        isStudentProfilePlatform,
        adminUrl,
      }) => {
        if (isStudentProfilePlatform) {
          goToRelativeRoute({
            route: sentralActivity.studentProfileUrl,
          });
        }

        if (adminUrl) {
          goToRelativeRoute({
            route: adminUrl,
          });
        }

        if (redirectUrl) {
          setIsSentralLoginActivityLogged({
            ...sentralActivity,
            isLoginTriggered: true,
            openInNewTab: false,
            platformName: "",
            studentId: "",
            studentProfileUrl: "",
            adminUrl: "",
          });
          setSentralPortalDetails({
            url: redirectUrl,
            isVisible: true,
            platformName: "",
            loader: false,
          });

          refetchNotificationStateQuery();
        } else {
          window.parent.postMessage(
            {
              type: CLOSE_SENTRAL_PORTAL_IFRAME,
              message: "Something went wrong",
            },
            "*"
          );
          setIsSentralLoginActivityLogged({
            ...sentralActivity,
            isLoginTriggered: true,
            openInNewTab: false,
            platformName: "",
            studentId: "",
          });
        }
      };

      const handleVerificationFailed = ({
        sentralActivity,
        isStudentProfilePlatform,
        adminUrl,
      }) => {
        if (isStudentProfilePlatform) {
          goToRelativeRoute({
            route: sentralActivity.studentProfileUrl,
          });
        }

        if (adminUrl) {
          goToRelativeRoute({
            route: adminUrl,
          });
        }

        setIsSentralLoginActivityLogged({
          ...sentralActivity,
          isLoginTriggered: true,
          openInNewTab: false,
          platformName: "",
          studentId: "",
          studentProfileUrl: "",
          adminUrl: "",
        });

        setSentralPortalDetails({
          isVisible: isStudentProfilePlatform,
          verificationFailed: isStudentProfilePlatform,
          platformName: "",
          loader: false,
        });
      };

      return {
        onPusherEventTriggered: params => {
          const event = _.get(params, "event", "");
          const redirectUrl = _.get(params, "redirectUrl", "");
          const sentralActivity = getIsSentralLoginActivityLogged();
          const isStudentProfilePlatform =
            sentralActivity.platformName === "student_profile";
          const adminUrl = sentralActivity.adminUrl;

          switch (event) {
            case "sentralOauthUserVerificationComplete":
              handleVerificationComplete({
                redirectUrl,
                sentralActivity,
                isStudentProfilePlatform,
                adminUrl,
              });
              break;
            case "sentralOauthUserVerificationFailed":
              handleVerificationFailed({
                sentralActivity,
                isStudentProfilePlatform,
                adminUrl,
              });
              break;
            default:
              break;
          }
        },
      };
    },
  }),
  withPusherBind([
    "sentralOauthUserVerificationFailed",
    "sentralOauthUserVerificationComplete",
  ])
)(SentralPortalModal);
